import { Helmet } from "react-helmet-async";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FAQ } from "@/components/FAQ";
import { useLanguage } from "@/contexts/LanguageContext";
import { faqPageTranslations, faqCategories, getText } from "@/components/faq/faqData";
import { Link } from "react-router-dom";

const seo: Record<string, { title: string; description: string }> = {
  de: {
    title: "FAQ – Häufige Fragen zu EAP® Abutments",
    description: "Antworten zu Bestellung, Design, Material, Abformung und Versand der patientenindividuellen EAP® Titan-Abutments.",
  },
  en: {
    title: "FAQ – Frequently Asked Questions | EAP® Abutments",
    description: "Answers on ordering, design, material, impression and shipping of patient-specific EAP® titanium abutments.",
  },
  fr: {
    title: "FAQ – Questions fréquentes | EAP® Abutments",
    description: "Réponses sur la commande, le design, le matériau, l'empreinte et l'expédition des piliers EAP® en titane.",
  },
  nl: {
    title: "FAQ – Veelgestelde vragen | EAP® Abutments",
    description: "Antwoorden over bestelling, design, materiaal, afdruk en verzending van patiëntspecifieke EAP® titanium abutments.",
  },
  it: {
    title: "FAQ – Domande frequenti | EAP® Abutments",
    description: "Risposte su ordine, design, materiale, impronta e spedizione degli abutment EAP® in titanio specifici per il paziente.",
  },
  es: {
    title: "FAQ – Preguntas frecuentes | EAP® Abutments",
    description: "Respuestas sobre pedido, diseño, material, impresión y envío de los pilares EAP® de titanio específicos del paciente.",
  },
};

// The FAQPage schema is generated from the same data that is rendered on the
// page, so structured data and visible content can never diverge.
const buildFaqJsonLd = (language: string) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "inLanguage": language,
  "mainEntity": Object.values(faqCategories)
    .flat()
    .map((item) => ({
      "@type": "Question",
      "name": getText(item.question, language),
      "acceptedAnswer": {
        "@type": "Answer",
        "text": getText(item.answer, language),
      },
    })),
});

const FAQPage = () => {
  const { language } = useLanguage();
  const t = faqPageTranslations;
  const meta = seo[language] || seo.en;
  const faqJsonLd = buildFaqJsonLd(language);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href="https://eap-abutments.com/faq" />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content="https://eap-abutments.com/faq" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <Navigation />
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-4">
          <Breadcrumbs items={[{ label: "FAQs" }]} />
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
              {getText(t.title, language)}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {getText(t.subtitle, language)}
            </p>
          </div>
          <FAQ />
          <nav aria-label={language === "de" ? "Weiterführende Seiten" : "Related pages"} className="max-w-4xl mx-auto mt-16">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              {language === "de" ? "Weiterführende Seiten" : "Related pages"}
            </h2>
            <ul className="grid gap-3 sm:grid-cols-3">
              <li>
                <Link to="/research" className="block h-full rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors">
                  <span className="font-medium text-foreground">
                    {language === "de" ? "Wissenschaftliche Literatur" : "Scientific literature"}
                  </span>
                </Link>
              </li>
              <li>
                <Link to="/fuer-patienten" className="block h-full rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors">
                  <span className="font-medium text-foreground">
                    {language === "de" ? "Informationen für Patienten" : "Information for patients"}
                  </span>
                </Link>
              </li>
              <li>
                <Link to="/ti-base-oder-individuelles-abutment" className="block h-full rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors">
                  <span className="font-medium text-foreground">
                    {language === "de" ? "Ti-Base oder individuelles Abutment" : "Ti-base or custom abutment"}
                  </span>
                </Link>
              </li>
            </ul>
          </nav>

        </div>
      </main>
      <Footer />
    </div>
  );
};

export default FAQPage;
